import { useState, useLayoutEffect, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { tokens, Loader, Link } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';

import { Page } from '@hotjar/webapp/components/Page';
import { Icon } from '@hotjar/webapp/components/Icon';
import { Accordion, AccordionSection } from '@hotjar/webapp/components/Accordion';

import { usePlanRepository } from '@hotjar/webapp/hooks/usePlanRepository';
import { usePageTitle } from '@hotjar/webapp/hooks/usePageTitle';

import { useNavigate, useLocation } from 'react-router-dom';

import type { Site } from '@hotjar/site';
import { useSites, useCurrentSiteId } from '@hotjar/site';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { SITE } from '@hotjar/webapp/constants/routes';
import { SiteInformations } from '../SiteInformations';
import { DataSuppression } from '../DataSuppression';
import { SessionTargetingTracking } from '../SessionTargetingTracking';
import { IpBlocking } from '../IpBlocking';
import SessionCapture from '../SessionCapture';
import { ContentHeader } from './ContentHeader';

const SECTIONS = ['site-information', 'targeting', 'suppression', 'ip-blocking'];

const S = {
  Breadcrumb: styled.div`
    display: flex;
    align-items: center;
  `,
  Title: styled.h2`
    font-size: 20px;
    line-height: 27px;
    letter-spacing: 0.15px;
    color: ${tokens.color.text_default};
    font-weight: 600;
    margin: 0;
  `,
  Icon: styled(Icon)`
    margin: 0 15px;
  `,
  ContentWrapper: styled.div`
    width: 740px;
    margin: 50px auto;
  `,
};

export const SiteSettingsPage = (): JSX.Element => {
  const { t } = useTranslation();
  usePageTitle(t('common:site-settings.page-title'));
  const siteId = useCurrentSiteId();
  const { query: sitesQuery, invalidate } = useSites();
  const { data: sites } = sitesQuery;
  const navigate = useNavigate();
  const { hash, pathname } = useLocation();
  const [openedSection, setOpenedSection] = useState(
    SECTIONS.findIndex((section) => section === hash.slice(1)),
  );
  const selectedSite = sites?.find((site: Site) => siteId === String(site.id));
  const [siteHasCC, setSiteHasCC] = useState(false);

  const planRepository = usePlanRepository();

  useEffect(() => {
    // When navigating to /site/*/settings we should update the url with the siteId
    // or navigate to the overview if there is no siteId in the cache.
    if (pathname.includes('*')) {
      if (siteId) {
        navigate(getRoute(SITE.SETTINGS.path, { siteId }));
      } else {
        navigate(getRoute(SITE.LIST.path));
      }
    }
  }, [pathname, siteId]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * TODO https://hotjar.atlassian.net/browse/PYM-945 block navigation when unsaved changes.
   * This used to be handled in angular, but later disabled as it was causing issues. In PYM-945
   * we'll introduce this functionality again.
   */
  const onFormsDirty = () => {
    /** noop */
  };

  useLayoutEffect(() => {
    if (
      selectedSite &&
      selectedSite.effectivePlanId &&
      planRepository.byId(selectedSite.effectivePlanId).ccDailyAllowance > 0
    ) {
      setSiteHasCC(true);
    } else {
      setSiteHasCC(false);
    }
  }, [planRepository, selectedSite]);

  const setUrlHashFromSection = (sectionIndex: number) => {
    if (sectionIndex !== -1) {
      navigate(`#${SECTIONS[sectionIndex]}`);
    }
    setOpenedSection(sectionIndex);
  };

  const changeSite = (newSiteId: number, urlHash: string) => {
    navigate({
      pathname: getRoute(SITE.SETTINGS.path, { siteId: newSiteId }),
      hash: urlHash ? `#${urlHash}` : undefined,
    });
  };

  return (
    <Page>
      <S.Breadcrumb>
        <Link kind="inline" href={getRoute(SITE.LIST.path)}>
          {t('common:site-settings.sites-organizations')}
        </Link>
        <S.Icon color={tokens.color.neutral_600} icon="caret-right" size={16} />
        <S.Title>{t('common:site-settings.title')}</S.Title>
      </S.Breadcrumb>

      {selectedSite ? (
        <S.ContentWrapper>
          <ContentHeader selectedSite={selectedSite} sites={sites ?? []} changeSite={changeSite} />
          {siteHasCC && <SessionCapture site={selectedSite} />}
          <Accordion
            onChange={setUrlHashFromSection}
            sectionToShow={openedSection}
            customCss={css`
              margin-top: 24px;
            `}
            disableControl
          >
            <AccordionSection
              iconName="details"
              sectionTitle={t('common:site-settings.site-information.title')}
              sectionDescription={t('common:site-settings.site-information.description')}
              dataTestId="site-information"
            >
              <SiteInformations
                site={selectedSite}
                onFormDirty={onFormsDirty}
                onFormSaved={invalidate}
              />
            </AccordionSection>
            <AccordionSection
              iconName="crosshair"
              sectionTitle={t('common:site-settings.targeting-tracking.title')}
              sectionDescription={
                siteHasCC
                  ? t('common:site-settings.targeting-tracking.description-with-cc')
                  : t('common:site-settings.targeting-tracking.description-without-cc')
              }
              dataTestId="targeting"
            >
              <SessionTargetingTracking site={selectedSite} onFormDirty={onFormsDirty} />
            </AccordionSection>
            <AccordionSection
              iconName="hidden"
              sectionTitle={t('common:site-settings.data-suppression.title')}
              sectionDescription={t('common:site-settings.data-suppression.description')}
              dataTestId="suppression"
            >
              <DataSuppression
                site={selectedSite}
                onFormDirty={onFormsDirty}
                onFormSaved={invalidate}
              />
            </AccordionSection>
            <AccordionSection
              iconName="ip"
              sectionTitle={t('common:site-settings.ip-blocking.title')}
              sectionDescription={t('common:site-settings.ip-blocking.description')}
              dataTestId="ip-blocking"
            >
              <IpBlocking site={selectedSite} onFormDirty={onFormsDirty} onFormSaved={invalidate} />
            </AccordionSection>
          </Accordion>
        </S.ContentWrapper>
      ) : (
        <Loader
          size="l"
          css={css`
            margin-top: 65px;
          `}
        />
      )}
    </Page>
  );
};
